import React from 'react';
import { Box, Pressable } from '@gluestack-ui/themed';

export interface TuckedStackSheet {
  key: string;
  onPress: () => void;
  accessibilityLabel: string;
  children: React.ReactNode;
}

interface TuckedStackProps {
  children: React.ReactNode;
  sheets: TuckedStackSheet[];
}

/** How much of each sheet stays visible below the card in front of it. */
const PEEK = 52;
/** How much of each sheet is hidden behind the card above it. */
const TUCK = 16;

/**
 * TuckedStack
 * ----
 * Sheets sit behind the front card and only their lower edge peeks out.
 */
function TuckedStack({ children, sheets }: TuckedStackProps) {
  if (sheets.length === 0) {
    return children;
  }

  return (
    <Box testID="tucked-stack" position="relative" pb={sheets.length * PEEK}>
      {sheets.map((sheet, index) => (
        <Pressable
          key={sheet.key}
          testID={`tucked-stack-sheet-${sheet.key}`}
          onPress={sheet.onPress}
          accessibilityRole="button"
          accessibilityLabel={sheet.accessibilityLabel}
          position="absolute"
          left="$3"
          right="$3"
          bottom={(sheets.length - 1 - index) * PEEK}
          zIndex={sheets.length - index}
          borderWidth={1}
          borderTopWidth={0}
          borderColor="$borderMedium"
          bg="$bgSubtle"
          borderBottomLeftRadius="$md"
          borderBottomRightRadius="$md"
          height={PEEK + TUCK}
          minHeight={48}
          px="$4"
          pt={TUCK}
          justifyContent="center"
          $hover={{ bg: '$bgHover' }}
        >
          {sheet.children}
        </Pressable>
      ))}
      <Box testID="tucked-stack-front" position="relative" zIndex={sheets.length + 1}>
        {children}
      </Box>
    </Box>
  );
}

export default TuckedStack;
